import React, { useState } from 'react';
import {
  Badge,
  Button,
  Datagrid,
  Icon,
  Menu,
  MenuButton,
  Spacer,
  Stack,
  Title
} from '@efast_public/fjd-component-library';
import { useTranslation } from 'react-i18next';
import { UserAndRoleManagementModal } from '../../../../modals/UserAndRoleManagementModal';
import { IUserRoleDto } from '../../../../api/models/Einstellungen/userRoleDto';
import {
  isLastAdministrator,
  isUserAdministrator,
  isUserRegistered
} from './utils/usersAndRoles';
import { useQuery } from '@tanstack/react-query';
import { BehApiRoutes } from '../../../../api/beh-api';
import UsersAndRolesManagementEditModal from './usersAndRolesManagement/UsersAndRolesManagementEditModal';
import DeleteUsersAndRolesManagementDeleteDialog from './usersAndRolesManagement/UsersAndRolesManagementDeleteDialogModal';
import { useReadUserInviteConfig } from '../../../../api/hooks/useReadUserInviteConfig';

export default function UsersAndRoles() {
  const { t } = useTranslation();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{
    user: IUserRoleDto;
    mode?: 'edit' | 'delete';
  } | null>(null);

  const { data: inviteConfig } = useReadUserInviteConfig();
  const {
    refetch: loadUsersRoles,
    data: userRolesData,
    isLoading: isValidating
  } = useQuery<Array<IUserRoleDto>>({
    queryKey: [BehApiRoutes.userRoles()]
  });

  return (
    <Stack spacing="l">
      <div style={{ width: '50rem' }}>
        <Stack spacing="l">
          <Title level={2} visualLevel={4}>
            {t('usersAndRoles.headline')}
          </Title>
          <div>{t('usersAndRoles.descriptionText')}</div>
          <div style={{ width: '200px' }}>
            <Button onClick={() => setIsModalVisible(true)}>
              {t('usersAndRoles.addUserButton')}
            </Button>
          </div>
        </Stack>
      </div>
      <Spacer />
      <Datagrid<IUserRoleDto>
        columns={[
          {
            id: 'lastName',
            header: 'Nachname',
            accessorKey: 'lastName',
            enableHiding: true,
            size: 150,
            minSize: 2,
            enableSorting: true
          },
          {
            id: 'firstName',
            header: 'Vorname',
            accessorKey: 'firstName',
            enableHiding: true,
            size: 150,
            minSize: 4,
            enableSorting: true,
            enableResizing: true
          },
          {
            id: 'email',
            header: 'E-Mail Adresse',
            accessorKey: 'email',
            enableHiding: true,
            size: 300,
            minSize: 4,
            enableSorting: true,
            enableResizing: true
          },
          {
            id: 'groups',
            header: 'Gruppen und Rollen',
            accessorKey: 'groups',
            enableHiding: true,
            size: 600,
            minSize: 4,
            enableSorting: false,
            sortingFn: 'alphanumeric',
            enableResizing: true,
            cell: (row) => (
              <div>
                {row.row.original.groups
                  .filter((subGroup) => subGroup.roles.length > 0)
                  .map((role) => (
                    <div>
                      {role.name}: {role.roles.at(-1)?.name}
                    </div>
                  ))}
              </div>
            )
          },
          {
            id: 'isAdmin',
            header: 'Admin',
            accessorKey: 'isAdmin',
            enableHiding: true,
            size: 150,
            minSize: 4,
            enableSorting: false,
            enableResizing: true,
            cell: (row) =>
              isUserAdministrator(row.row.original) && (
                <Icon variant="success" name="checkmark-filled" />
              )
          },
          {
            id: 'status',
            header: 'Status',
            accessorKey: 'status',
            enableHiding: true,
            size: 10,
            minSize: 4,
            enableSorting: false,
            enableResizing: true,
            cell: (row) =>
              isUserRegistered(row.row.original) ? (
                <Badge
                  size="sm"
                  iconBefore="checkmark-filled"
                  variant="success"
                >
                  registriert
                </Badge>
              ) : (
                <Badge size="sm" variant="inProgress">
                  eingeladen
                </Badge>
              )
          },
          {
            id: 'menu',
            header: 'Aktionen',
            accessorKey: 'menu',
            enableHiding: true,
            size: 4,
            minSize: 2,
            enableSorting: false,
            enableResizing: false,
            cell: (row) => (
              <MenuButton variant="primary-text" label="menu">
                <Menu.MenuItem
                  icon="edit"
                  label={t('usersAndRoles.editUserButton')}
                  onClick={() =>
                    setSelectedUser({
                      user: row.row.original,
                      mode: 'edit'
                    })
                  }
                />
                <Menu.MenuItem
                  icon="trashcan"
                  label={t('usersAndRoles.deleteUserButton')}
                  onClick={() =>
                    setSelectedUser({
                      user: row.row.original,
                      mode: 'delete'
                    })
                  }
                />
              </MenuButton>
            )
          }
        ]}
        data={userRolesData ?? []}
        labels={{
          currentPage: 'Aktuelle Seite',
          detailButton: 'Weitere Infos ein-/ausblenden',
          emptyData: 'Keine Einträge',
          nextPage: 'Nächste Seite',
          of: 'Seite {0} von {1}',
          page: 'Seite',
          pageSize: '{0} Einträge pro Seite',
          previousPage: 'Vorherige Seite',
          rows: 'Zeilen',
          tableTitle: '',
          // tableTitle: t('usersAndRoles.tableHeadline'),
          ascBtn: 'Aufsteigend sortieren',
          descBtn: 'Absteigend sortieren',
          hideBtn: 'Spalte ausblenden'
        }}
        manualPagination={false}
        isDataLoading={isValidating}
        dataKey="email"
        manualSorting={false}
        areColumnsHideable
        areColumnsResizeable
        tableHeight={540}
        clickableRows={false}
      />
      {isModalVisible && (
        <UserAndRoleManagementModal
          onClose={() => setIsModalVisible(false)}
          isOpen
        />
      )}
      {selectedUser?.mode === 'edit' && userRolesData && (
        <UsersAndRolesManagementEditModal
          isLastAdministrator={isLastAdministrator(userRolesData)}
          keycloakGroupRoles={inviteConfig}
          userRolesAndGroups={selectedUser?.user}
          onClose={() => {
            setSelectedUser(null);
          }}
          loadUsersRoles={loadUsersRoles}
          headerText={'Benutzer:in verwalten'}
          isOpen
        />
      )}
      {selectedUser?.mode === 'delete' && (
        <DeleteUsersAndRolesManagementDeleteDialog
          loadUsersRoles={loadUsersRoles}
          onClose={() => setSelectedUser(null)}
          userId={selectedUser.user?.id ?? ''}
          isOpen
        />
      )}
    </Stack>
  );
}
